import React = require("react");
import { Activity, fieldValidation, PublicAmorgUser } from "../../../amorg-common";
import { updateActivity } from "../../../api/activityClientApi";
import { EditableText } from "../../../designSystem/compositeComponents/EditableText";
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { DateStartEndTimeFields } from "../../../designSystem/formField/DateStartEndTimeFields";
import { FormField } from "../../../designSystem/formField/FormField";
import { TextSaveField } from "../../../designSystem/formField/text/TextSaveField";
import { TextAreaSaveField } from "../../../designSystem/formField/textArea/TextAreaSaveField";
import { LoadingPageOverlay } from "../../../designSystem/LoadingPageOverlay";
import { MessageCard } from "../../../designSystem/Message";
import { PageSection } from "../../../designSystem/PageSection";
import { MeetingParticipantList } from "../participants/MeetingParticipantList";
import { FileLinkPageSection } from "../../../designSystem/compositeComponents/FileLinkCommentPageSection";


type MeetingDetailsPanelProps = {
    componentTitle?: string,
    activity: Activity;
    periodSelection: 'date-time' | 'date',
    infoComponents?: React.ReactNode,
    extraComponents?: React.ReactNode,
    titleText?: string,
    descriptionText?: string,
    users: PublicAmorgUser[],
    participantTexts?: { header: string };
    onActivityUpdated: () => void;
};
export function MeetingDetailsPanel (props: MeetingDetailsPanelProps)
{
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState('');
    const [titleError, setTitleError] = React.useState<string>('');
    
    async function performUpdate (mutator: (activity: Activity) => void)
    {
        //const a = await getActivity(initialActivity.id);
        mutator(props.activity);
        const result = await updateActivity(
            props.activity.id,
            props.activity.title,
            props.activity.description,
            props.activity.start,
            props.activity.end,
            props.activity.location
        );
        if (result.errorMessage)
            setError('Der opstod en fejl ved opdatering af mødet. Prøv igen eller kontakt support');
        else
            setError('');
        
        props.onActivityUpdated();
        
        return result;
    }

    async function handleUpdateDates (start: Date, end: Date)
    {
        performUpdate((activity) =>
        {
            activity.start = start;
            activity.end = end;
        });
    }

    async function handleSaveTitle (value: string):Promise<boolean>
    {
        setLoading(true);
        const result = await updateActivity(props.activity.id, value, props.activity.description, props.activity.start, props.activity.end, props.activity.location);
        if(result.isSuccess)
        {
            setTitleError('');
            await props.onActivityUpdated();
            setLoading(false);
            return true;
        }
        else
        {
            setTitleError('Ugyldig titel');
            setLoading(false);
            return false;
        }
    }

    return (

        <Flex flexDirection="column" gap={32}>

            <LoadingPageOverlay loading={loading} />
            
            {props.activity &&
                <>
                    {error && <MessageCard variant="error">{error}</MessageCard>}

                    <Flex flexDirection="column" gap={8}>

                        <EditableText ariaLabel='Rediger mødetitel' placeholder='Mødetitel' 
                            value={props.activity.title}
                            errorMessage={titleError}
                            onSaveWithReturnValue={handleSaveTitle}
                            styleMode="H2"
                        />

                        <PageSection
                            contentStyleVariant='card'
                        >
                            <Flex flexWrap="wrap">
                                <DateStartEndTimeFields
                                    hideLabels
                                    start={props.activity.start}
                                    end={props.activity.end}
                                    onChanged={handleUpdateDates} />

                                <TextSaveField
                                    trailingIcon={"interface_glyph_location"}
                                    hideLabel
                                    label={""}
                                    formFieldStyle={{ flex: 1 }}
                                    reserveLabelSpace={true}
                                    value={props.activity.location}
                                    
                                    onSave={newValue =>
                                    {
                                        if (fieldValidation.activity.location(newValue))
                                            performUpdate((a) => { a.location = newValue; });
                                    }}
                                    validator={(currentValue) =>
                                        fieldValidation.activity.location(currentValue)
                                            ? { valid: true }
                                            : { valid: false, errorMessage: 'Ugyldig beskrivelse. Maks. 200 tegn.' }
                                    }
                                    />
                            </Flex>

                            <TextAreaSaveField
                                label={"Mødebeskrivelse"}
                                minRows={3}
                                value={props.activity.description}
                                onSave={newValue =>
                                {
                                    if (fieldValidation.activity.description(newValue))
                                        performUpdate((a) => { a.description = newValue; });
                                }}
                                validator={(currentValue) =>
                                    fieldValidation.activity.description(currentValue)
                                        ? { valid: true }
                                        : {
                                            valid: false, errorMessage: `Ugyldig beskrivelse. Maks. 10000 tegn (${currentValue.length}).`
                                        }}
                            />

                            <FileLinkPageSection
                                fileReference={{ id:props.activity.id, type:"Meeting" }}
                                linkReference={{ id:props.activity.id, type:"Meeting"}}
                            />

                        </PageSection>
                    </Flex>

                    
                    <PageSection
                        header={{ title: 'Deltagere'}}
                        headerStyleVariant='card'
                        contentStyleVariant='card'
                    >
                        {props.participantTexts &&
                            <FormField
                                label={""}
                                inputComponent={
                                    <>
                                        <MeetingParticipantList
                                            activity={props.activity}
                                            users={props.users}
                                            onParticipantsUpdated={props.onActivityUpdated} />
                                    </>
                                } />
                        }    
                    </PageSection>
                </>
            }
        </Flex>
    );
}